import { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import type { Centre } from '@/api/centresApi';
import { Seo } from '@/seo/Seo';
import {
  breadcrumbSchema,
  collectionPageSchema,
  faqSchema,
  itemListSchema,
} from '@/seo/schemas';
import { findCity, CITY_SLUGS, CITIES_INDEX_PATH, nearestCities } from '@/data/cities';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { resolveDefaultImages } from '@/utils/centreImages';
import { sectorsByCity, SECTORS } from '@/data/sectors';
import { PROBLEMS } from '@/data/problems';
import { parseCityAndSector, deslugify } from '@/utils/slug';
import { CITY_PREFIX, cityPath, sectorPath, centreSeoPath } from '@/utils/seoPaths';
import { cityTitle, cityH1, cityDescription } from '@/seo/meta';
import { LocalProof } from '@/components/seo/LocalProof';
import { collectLocalProof } from '@/seo/localProof';
import { clinicListSchema } from '@/seo/schemas';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { CentreList, NEARBY_RADIUS_KM } from '@/components/seo/CentreList';
import { LIST_LIMIT, robotsFor } from '@/seo/inventoryGate';
import { RelatedSearches } from '@/components/seo/RelatedSearches';
import {
  SeoPage,
  SeoHero,
  SeoSection,
  SeoFaq,
  SeoLinkTile,
} from '@/components/seo/SeoLayout';
import SectorService from './SectorService';
import { useCatalog } from '@/context/CatalogContext';
import { TOP_SERVICE_SLUGS } from '@/seo/topServices';
import { serviceDisplayName } from '@/seo/meta';


const TOP_CONDITION_SLUGS = [
  'back-pain',
  'neck-pain',
  'sciatica',
  'frozen-shoulder',
  'knee-pain',
  'arthritis',
];

export default function CityHub() {
  // Mounted via SeoDispatcher when the slug starts with "physiotherapy-in-".
  const { slug } = useParams<{ slug: string }>();
  const rest = slug?.startsWith(CITY_PREFIX) ? slug.slice(CITY_PREFIX.length) : '';
  const parsed = parseCityAndSector(rest, CITY_SLUGS, SECTORS);
  const { default_centre_image } = useSiteConfig();
  const defaultImage = resolveDefaultImages(default_centre_image)[0];

  // Inventory-gated indexation: null = not loaded yet (stay indexable);
  // [] after load = no local clinics → noindex until inventory exists.
  const [nearbyCentres, setNearbyCentres] = useState<Centre[] | null>(null);
  const { serviceBySlug } = useCatalog();

  // A sub-region in the legacy flat slug is handled by SectorService.
  if (parsed.sectorSlug) return <SectorService hubOnly />;

  const city = findCity(parsed.citySlug);
  if (!city) return <Navigate to="/centres" replace />;

  const sectors = sectorsByCity(city.slug);
  const path = cityPath(city.slug);
  const clinicHref = (c: Centre) => centreSeoPath(c) ?? `/centres/${c.id}`;
  const clinicUrl = (c: Centre) => `https://www.recovry.in${clinicHref(c)}`;
  const proof = collectLocalProof(nearbyCentres ?? [], clinicHref);

  const title = cityTitle(city.name);
  const description = cityDescription(city.name, nearbyCentres?.length || undefined);

  const sectorListSchema = itemListSchema({
    name: `Physiotherapy localities in ${city.name}`,
    url: `https://www.recovry.in${path}`,
    items: sectors.map((s) => ({
      name: s.fullName,
      url: `https://www.recovry.in${sectorPath(city.slug, s.slug)}`,
    })),
  });

  const cityFaqs = [
    {
      question: 'How do I check a physiotherapist is qualified?',
      answer:
        'Ask for their BPT or MPT degree and registration. Physiotherapists in India register with the Indian Association of Physiotherapists and, under the 2021 Act, with the National Commission for Allied and Healthcare Professions. Recovry checks these documents for every clinic before it is listed.',
      links: [
        { label: 'Indian Association of Physiotherapists', href: 'https://physiotherapyindia.org/' },
        { label: 'NCAHP', href: 'https://ncahp.abdm.gov.in/' },
      ],
    },
    {
      question: `How do I choose a physiotherapy clinic in ${city.name}?`,
      answer: `Start with location and speciality. Sort ${city.name} clinics by distance, check which therapies each one offers, and read patient reviews. Every clinic on Recovry is verified by our team before it goes live.`,
    },
    {
      question: `What does a physiotherapy session cost in ${city.name}?`,
      answer:
        proof.priceFrom != null && proof.priceTo != null
          ? `Clinics on Recovry in ${city.name} currently list sessions from ₹${proof.priceFrom} to ₹${proof.priceTo}. Neuro rehab, ACL recovery and home visits sit at the higher end. Each clinic's exact price is shown before you book.`
          : `Most ${city.name} clinics charge ₹500–₹1,500 per session. Neuro rehab, ACL recovery and home visits sit at the higher end. Each clinic's exact pricing is shown before you book.`,
    },
    {
      question: `Can a physiotherapist visit me at home in ${city.name}?`,
      answer: `Yes. Many Recovry-verified clinics in ${city.name} offer home physiotherapy alongside in-clinic sessions. Home-visit availability and any travel charge are listed on the clinic page.`,
    },
    {
      question: `What do people in ${city.name} usually come in for?`,
      answer: `Most commonly ${city.commonAilments.slice(0, 3).join(', ').toLowerCase()}, along with post-surgery rehabilitation. Clinics here treat the full range of musculoskeletal, sports and neurological conditions.`,
    },
    {
      question: `How many sessions will I need?`,
      answer: `Most musculoskeletal problems ease over 6–12 sessions. Severe injuries, post-surgical cases and chronic conditions take longer. Your physiotherapist sets a plan after the first assessment, so you know the likely course up front.`,
    },
    {
      question: `Do I need a doctor's referral?`,
      answer: `No. You can book a physiotherapist in ${city.name} directly. Bring any scans or reports you already have — they help the therapist assess you faster.`,
    },
  ];

  return (
    <SeoPage>
      <Seo
        title={title}
        description={description}
        canonical={path}
        robots={robotsFor('hub', nearbyCentres?.length)}
        keywords={`physiotherapy in ${city.name}, physiotherapy clinic ${city.name}, physiotherapist near me ${city.name}, back pain therapy ${city.name}, knee pain ${city.name}, sports injury rehab ${city.name}`}
        schema={[
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Find a Centre', path: '/centres' },
            { name: city.name, path },
          ]),
          collectionPageSchema({
            name: `Physiotherapy & Rehab Clinics in ${city.name}`,
            description,
            url: `https://www.recovry.in${path}`,
          }),
          sectorListSchema,
          ...(nearbyCentres?.length
            ? [clinicListSchema({
                name: `Physiotherapy clinics in ${city.name}`,
                url: `https://www.recovry.in${path}`,
                centres: nearbyCentres,
                linkFor: (c) => clinicUrl(c as Centre),
                defaultImage,
              })]
            : []),
          faqSchema(cityFaqs),
        ]}
      />

      <Breadcrumbs
        items={[
          { name: 'Home', path: '/' },
          { name: 'Find a Centre', path: '/centres' },
          { name: city.name, path },
        ]}
      />

      <SeoHero
        title={cityH1(city.name)}
        lede={city.intro}
        tags={city.commonAilments}
        tagsLabel={`What people in ${city.name} come in for`}
        primaryCta={{ label: 'Browse all clinics', to: '/centres' }}
        secondaryCta={{ label: 'List your clinic', to: '/partners' }}
      />

      <CentreList
        near={{ lat: city.lat, lng: city.lng }}
        radiusKm={NEARBY_RADIUS_KM}
        onResults={setNearbyCentres}
        limit={LIST_LIMIT.hub}
        heading={`Verified clinics in ${city.name}`}
        subheading="Closest to the city centre first. Tap a clinic to see services, pricing and open hours."
        linkFor={centreSeoPath}
        moreHref="/centres"
        moreLabel={`See every clinic in ${city.name}`}
        emptyMessage={`No Recovry-verified clinics in ${city.name} yet. We onboard new clinics every week - or list yours and be the first here.`}
      />

      <LocalProof data={proof} place={city.name} />

      {sectors.length > 0 && (
        <SeoSection
          title={`Areas we cover in ${city.name}`}
          description="Jump straight to clinics in your part of the city."
        >
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {sectors.map((s) => (
              <li key={s.slug}>
                <SeoLinkTile
                  to={sectorPath(city.slug, s.slug)}
                  title={s.displayName}
                  meta={`Physiotherapists in ${s.fullName}`}
                />
              </li>
            ))}
          </ul>
        </SeoSection>
      )}

      <SeoSection
        title={`Conditions treated in ${city.name}`}
        description="What each condition involves, and which clinics nearby treat it."
      >
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {TOP_CONDITION_SLUGS.map((conditionSlug) => {
            const p = PROBLEMS.find((x) => x.slug === conditionSlug);
            if (!p) return null;
            return (
              <li key={conditionSlug}>
                <SeoLinkTile
                  to={`/conditions/${conditionSlug}-in-${city.slug}`}
                  title={`${p.name} in ${city.name}`}
                  meta={p.symptoms.slice(0, 2).join(' · ')}
                />
              </li>
            );
          })}
        </ul>
      </SeoSection>

      <SeoSection
        title={`Physiotherapy services in ${city.name}`}
        description="Every service below is offered by at least one verified clinic in the city."
      >
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {TOP_SERVICE_SLUGS.map((slug) => (
            <li key={slug}>
              <SeoLinkTile
                to={`/${slug}-in-${city.slug}`}
                title={serviceDisplayName(serviceBySlug(slug)?.name ?? deslugify(slug))}
                meta={`in ${city.name}`}
              />
            </li>
          ))}
        </ul>
      </SeoSection>

      <SeoSection title={`Physiotherapy in ${city.name}: common questions`}>
        <SeoFaq items={cityFaqs} />
      </SeoSection>

      <RelatedSearches
        groups={[
          {
            heading: 'Nearby cities',
            links: [
              ...nearestCities(city).map((c) => ({ label: `Physiotherapy in ${c.name}`, path: cityPath(c.slug) })),
              { label: 'All cities', path: CITIES_INDEX_PATH },
            ],
          },
          {
            heading: 'Specialised care',
            links: [
              { label: `Sports rehab in ${city.name}`, path: `/recovery/sports-rehab-in-${city.slug}` },
              { label: `Post-surgery rehab in ${city.name}`, path: `/recovery/post-surgery-rehab-in-${city.slug}` },
              { label: `Stroke recovery in ${city.name}`, path: `/recovery/stroke-recovery-in-${city.slug}` },
              { label: `Geriatric physio in ${city.name}`, path: `/recovery/geriatric-physio-in-${city.slug}` },
              { label: `Home physiotherapy in ${city.name}`, path: `/recovery/home-physio-in-${city.slug}` },
            ],
          },
          {
            heading: 'By condition',
            links: PROBLEMS.slice(0, 8).map((p) => ({
              label: `${p.name} treatment in ${city.name}`,
              path: `/conditions/${p.slug}-in-${city.slug}`,
            })),
          },
        ]}
      />
    </SeoPage>
  );
}
